/*
  BraceLinks — Daily Inspiration engine
  -------------------------------------------------------------------
  One shared module that powers BOTH the full Daily Inspiration page
  (two cards + EN/AR toggle + share) AND the homepage / product-page
  teasers. It fetches assets/daily-inspiration.json ONCE per page
  (module-level singleton) and reuses it for every <bracelinks-daily>
  element on the page.

  Rules (see build brief §7):
  - "Today" is defined in Asia/Beirut for every visitor worldwide, so
    everyone shares one daily rhythm.
  - Never blank: if a date has no entry (e.g. a visitor after the last
    authored day), fall back deterministically so the page works forever
    without intervention. The real calendar date is always shown.
  - EN/AR toggle persists in localStorage['bracelinks_lang']; Arabic
    renders right-to-left with the Arabic font stack.

  This file is a classic script (no ES modules) so Shopify can serve it
  directly. The pure date/resolve helpers are also exported under
  module.exports so they can be unit-tested in Node.
*/
(function () {
  'use strict';

  var TZ = 'Asia/Beirut';
  var LANG_KEY = 'bracelinks_lang';

  /* ----------------------------- Pure helpers ----------------------------- */

  // YYYY-MM-DD for "today" in Beirut. `now` is injectable for tests.
  function todayKey(now) {
    var d = now || new Date();
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  }

  // Whole days from one YYYY-MM-DD to another (UTC midnight basis).
  function daysBetween(fromKey, toKey) {
    var a = Date.parse(fromKey + 'T00:00:00Z');
    var b = Date.parse(toKey + 'T00:00:00Z');
    return Math.floor((b - a) / 86400000);
  }

  // Build a calendar-day index: MM-DD -> entry. First chronological occurrence
  // wins (so an overlapping date like 12-04 uses the earliest authored version).
  // Cached on the payload object so it's only built once per page.
  function monthDayIndex(payload) {
    if (payload.__byMonthDay) return payload.__byMonthDay;
    var days = payload.days || {};
    var keys = Object.keys(days).sort(); // chronological (YYYY-MM-DD sorts correctly)
    var index = {};
    for (var i = 0; i < keys.length; i++) {
      var md = keys[i].slice(5); // "MM-DD"
      if (!index[md]) index[md] = days[keys[i]];
    }
    payload.__byMonthDay = index;
    return index;
  }

  // Resolve the entry for a given date. The rotation REPEATS EVERY YEAR: the
  // same calendar day (MM-DD) always shows the same entry, regardless of year.
  // Leap day (02-29) falls back to 02-28. A deterministic cycle is the final
  // safety net so the page is never blank even with incomplete data.
  // Returns { entry, dateKey, fallback } or null if the data set is empty.
  function resolveEntry(payload, key) {
    var days = payload && payload.days ? payload.days : {};
    var keys = Object.keys(days).sort();
    if (!keys.length) return null;

    var index = monthDayIndex(payload);
    var md = key.slice(5); // MM-DD of the real (current) date

    if (index[md]) return { entry: index[md], dateKey: key, fallback: false };
    if (md === '02-29' && index['02-28']) {
      return { entry: index['02-28'], dateKey: key, fallback: true };
    }

    // Safety net (only if a calendar day is genuinely unauthored): cycle.
    var first = (payload.meta && payload.meta.first_date) || keys[0];
    var idx = ((daysBetween(first, key) % keys.length) + keys.length) % keys.length;
    return { entry: days[keys[idx]], dateKey: key, fallback: true };
  }

  // Levantine (Lebanese) Arabic month names — not the Gulf/MSA "يناير/فبراير…".
  var AR_MONTHS = [
    'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيّار', 'حزيران',
    'تمّوز', 'آب', 'أيلول', 'تشرين الأوّل', 'تشرين الثاني', 'كانون الأوّل',
  ];
  function toArabicDigits(value) {
    var glyphs = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return String(value).replace(/[0-9]/g, function (n) {
      return glyphs[+n];
    });
  }
  function formatLongDate(key, lang) {
    var parts = key.split('-'); // [YYYY, MM, DD]
    if (lang === 'ar') {
      var day = toArabicDigits(parseInt(parts[2], 10));
      var month = AR_MONTHS[parseInt(parts[1], 10) - 1] || '';
      var year = toArabicDigits(parts[0]);
      return day + ' ' + month + ' ' + year; // e.g. ٤ حزيران ٢٠٢٦
    }
    var d = new Date(key + 'T12:00:00Z'); // noon UTC avoids any day-shift
    try {
      return new Intl.DateTimeFormat('en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(d);
    } catch (e) {
      return key;
    }
  }

  function currentLang(force) {
    if (force === 'en' || force === 'ar') return force;
    try {
      return window.localStorage.getItem(LANG_KEY) === 'ar' ? 'ar' : 'en';
    } catch (e) {
      return 'en';
    }
  }

  function setLang(lang) {
    try {
      window.localStorage.setItem(LANG_KEY, lang);
    } catch (e) {
      /* private mode / storage disabled — ignore */
    }
  }

  /* ----------------- Singleton fetch (one request per page) ---------------- */

  var _cache = {}; // url -> Promise<payload>
  function loadData(url) {
    if (!_cache[url]) {
      _cache[url] = fetch(url, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('daily-inspiration fetch failed: ' + r.status);
        return r.json();
      });
    }
    return _cache[url];
  }

  /* ---------------------- Browser: custom element ------------------------- */

  if (typeof window !== 'undefined' && 'customElements' in window) {
    var BraceLinksDaily = class extends HTMLElement {
      connectedCallback() {
        this.url = this.dataset.src;
        this.forceLang = this.dataset.lang || null; // teaser locks EN
        this._payload = null;

        this._onClick = this.handleClick.bind(this);
        this.addEventListener('click', this._onClick);

        this._onVis = () => {
          if (document.visibilityState === 'visible' && this._payload) this.paint();
        };
        document.addEventListener('visibilitychange', this._onVis);

        // Restore dismissed "new here?" explainer.
        try {
          if (window.localStorage.getItem('bracelinks_daily_explainer') === '1') {
            var ex = this.querySelector('[data-daily-explainer]');
            if (ex) ex.hidden = true;
          }
        } catch (e) {
          /* ignore */
        }

        this.load();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('visibilitychange', this._onVis);
      }

      load() {
        if (!this.url) return;
        loadData(this.url)
          .then((payload) => {
            this._payload = payload;
            this.paint();
          })
          .catch(() => {
            // Fetch failed (offline). Leave skeleton, flag for optional CSS.
            this.setAttribute('data-error', '');
            this.removeAttribute('data-loading');
          });
      }

      paint() {
        if (!this._payload) return;
        var resolved = resolveEntry(this._payload, todayKey());
        if (!resolved) return;
        var lang = currentLang(this.forceLang);

        this.setAttribute('lang', lang);
        this.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');

        // Bilingual chrome (title, card headings, explainer, share, shop band).
        // Each element carries data-en / data-ar; we swap by current language.
        this.querySelectorAll('[data-i18n]').forEach((el) => {
          var v = lang === 'ar' ? el.getAttribute('data-ar') : el.getAttribute('data-en');
          if (v !== null && v !== '') el.textContent = v;
        });

        // Fill content fields. If a language is missing for a field, fall back
        // to English so the card is never blank, and keep that node LTR.
        this.querySelectorAll('[data-daily-field]').forEach((el) => {
          var f = el.getAttribute('data-daily-field');
          var usedLang = lang;
          var val = resolved.entry[f + '_' + lang];
          if (!val) {
            val = resolved.entry[f + '_en'] || '';
            usedLang = 'en';
          }
          el.textContent = val;
          if (usedLang === 'ar') {
            el.setAttribute('dir', 'rtl');
            el.classList.add('is-arabic');
          } else {
            el.removeAttribute('dir');
            el.classList.remove('is-arabic');
          }
        });

        // Today's real calendar date (long, localized).
        this.querySelectorAll('[data-daily-date]').forEach((el) => {
          el.textContent = formatLongDate(resolved.dateKey, lang);
        });

        // Reflect active language on toggle buttons.
        this.querySelectorAll('[data-daily-lang]').forEach((b) => {
          b.setAttribute(
            'aria-pressed',
            b.getAttribute('data-daily-lang') === lang ? 'true' : 'false'
          );
        });

        this.removeAttribute('data-loading');
      }

      handleClick(e) {
        var langBtn = e.target.closest('[data-daily-lang]');
        if (langBtn && this.contains(langBtn)) {
          e.preventDefault();
          setLang(langBtn.getAttribute('data-daily-lang'));
          this.paint();
          return;
        }
        var dismiss = e.target.closest('[data-daily-dismiss]');
        if (dismiss) {
          e.preventDefault();
          try {
            window.localStorage.setItem('bracelinks_daily_explainer', '1');
          } catch (err) {
            /* ignore */
          }
          var ex = this.querySelector('[data-daily-explainer]');
          if (ex) ex.hidden = true;
          return;
        }
        if (e.target.closest('[data-daily-share]')) {
          e.preventDefault();
          this.share();
        }
      }

      share() {
        if (!this._payload) return;
        var resolved = resolveEntry(this._payload, todayKey());
        if (!resolved) return;
        var lang = currentLang(this.forceLang);
        var scripture =
          resolved.entry['scripture_' + lang] || resolved.entry.scripture_en || '';
        var ref =
          resolved.entry['scripture_ref_' + lang] ||
          resolved.entry.scripture_ref_en ||
          '';
        var text = ref ? scripture + ' — ' + ref : scripture;
        var url = window.location.href;

        if (navigator.share) {
          navigator.share({ title: document.title, text: text, url: url }).catch(function () {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(text + ' ' + url)
            .then(() => this.toast())
            .catch(function () {});
        }
      }

      toast() {
        var el = this.querySelector('[data-daily-toast]');
        if (!el) return;
        el.classList.add('is-visible');
        window.clearTimeout(this._toastTimer);
        this._toastTimer = window.setTimeout(function () {
          el.classList.remove('is-visible');
        }, 2200);
      }
    };

    if (!customElements.get('bracelinks-daily')) {
      customElements.define('bracelinks-daily', BraceLinksDaily);
    }
  }

  /* ---- "Tap to receive": ripple + reveal today's word on tap (reusable) ---- */
  if (typeof document !== 'undefined') {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-tap-trigger]') : null;
      if (!trigger) return;
      var stage = trigger.closest('[data-tap]');
      if (!stage || stage.classList.contains('is-revealed')) return;
      e.preventDefault();
      stage.classList.add('is-tapped');
      trigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(function () {
        stage.classList.add('is-revealed');
      }, 220);
      var prompt = stage.querySelector('[data-tap-prompt]');
      if (prompt && prompt.getAttribute('data-revealed-text')) {
        prompt.textContent = prompt.getAttribute('data-revealed-text');
      }
    });
  }

  /* ------------------------- Node export for tests ------------------------ */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      todayKey: todayKey,
      daysBetween: daysBetween,
      resolveEntry: resolveEntry,
      formatLongDate: formatLongDate,
    };
  }
})();
